(() => {
  // Finds the main product section on a product page (its id ends in "__main").
  // Used to keep the Kbite app and this script focused on the main product,
  // not the product cards in the mega menu / related products.
  const getMainSection = () =>
    document.querySelector('[id^="shopify-section-"][id$="__main"]');

  class KbiteNotifyButton {
    constructor(container) {
      this.container = container;
      this.form = container.closest('form');
      // The size buttons sit outside the <form>, so we listen at the section level
      this.section = container.closest('[id^="shopify-section-"]') || this.form;
      this.variants = this.getVariants();

      this.update = this.update.bind(this);
      this.updateAfterVariantChange = this.updateAfterVariantChange.bind(this);

      this.listen();
      this.update();
    }

    getVariants() {
      const script = this.form?.querySelector('[data-kbite-variant-state-json]');

      try {
        return JSON.parse(script?.textContent || '{}');
      } catch (error) {
        console.warn('KBite variant data is invalid:', error);
        return {};
      }
    }

    getSelectedVariant() {
      const variantId = this.form?.querySelector('[name="id"]')?.value;

      return this.variants[variantId];
    }

    isSoldOut() {
      return this.getSelectedVariant()?.available === false;
    }

    // Wait and Save products: when the preorder button is showing for this size,
    // the customer can preorder it, so the Notify button is not needed.
    hasWaitAndSave() {
      const preorder = this.form?.querySelector('.preorder-btn');
      return !!preorder && preorder.style.display !== 'none';
    }

    show() {
      this.container.hidden = false;
      this.container.style.display = '';
      this.container.setAttribute('aria-hidden', 'false');
    }

    hide() {
      this.container.hidden = true;
      this.container.style.display = 'none';
      this.container.setAttribute('aria-hidden', 'true');
    }

    update() {
      if (this.isSoldOut() && !this.hasWaitAndSave()) {
        this.show();
        return;
      }

      this.hide();
    }

    updateAfterVariantChange(event) {
      // Only react to size changes inside this product's section
      if (event && event.target && this.section && !this.section.contains(event.target)) return;
      // Run just after the theme's own handlers, so the hidden variant id is updated first
      setTimeout(this.update, 0);
    }

    listen() {
      // Previously this listened on the <form> only. The size buttons are not inside
      // the form, so their change events never reached it and the Notify button never
      // showed. Listening on the document (filtered to this section) fixes that.
      document.addEventListener('change', this.updateAfterVariantChange);
      // The theme fires this after every variant change
      document.addEventListener('theme:variant:change', this.updateAfterVariantChange);
      window.addEventListener('load', this.update);
    }
  }

  /* Kbite (Cartbite) app fix.
     The app works out the selected size from the FIRST checked size button on the
     whole page. The mega menu product cards come before the main product, so it was
     reading a mega menu size (e.g. XS, in stock) and never rendering the Notify
     button. This points it at the main product section only.
     It also re-downloaded /products/<handle>.js on every size tap (twice per tap);
     the product data is now fetched once per page and reused. */
  const patchKbiteApp = () => {
    const app = window.growConfig;
    if (!app || typeof app.getVariantSelector !== 'function') return false;
    if (app.__commaPatched) return true;
    app.__commaPatched = true;

    const originalGetVariantSelector = app.getVariantSelector;
    app.getVariantSelector = function (index) {
      const main = getMainSection();
      // On a product page, only ever look at the main product's size buttons
      if (main) return Array.from(main.querySelectorAll(`[data-index="option${index}"]`));
      return originalGetVariantSelector.apply(this, arguments);
    };

    if (typeof app.getProduct === 'function') {
      const originalGetProduct = app.getProduct;
      let cachedProduct = null;
      app.getProduct = function (handle) {
        // Calls for a specific handle (e.g. collection pages) are left as they were
        if (handle) return originalGetProduct.apply(this, arguments);
        if (!cachedProduct) {
          cachedProduct = Promise.resolve(originalGetProduct.apply(this, arguments)).catch((error) => {
            cachedProduct = null; // allow a retry if the download failed
            throw error;
          });
        }
        return cachedProduct;
      };
    }

    // Re-run the app once now that it reads the right size (covers the size
    // selected when the page loaded)
    if (typeof app.onVariantChange === 'function') app.onVariantChange();
    return true;
  };

  // The app script loads on its own schedule, so check for it every 100ms (max 20s)
  const waitForKbiteApp = () => {
    if (patchKbiteApp()) return;
    let tries = 0;
    const timer = setInterval(() => {
      tries += 1;
      if (patchKbiteApp() || tries > 200) clearInterval(timer);
    }, 100);
  };

  const startKbiteNotifyButtons = () => {
    const containers = document.querySelectorAll('[data-kbite-notify-container]');

    containers.forEach((container) => {
      if (container.dataset.kbiteNotifyReady === 'true') return;

      container.dataset.kbiteNotifyReady = 'true';
      new KbiteNotifyButton(container);
    });

    if (containers.length) waitForKbiteApp();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', startKbiteNotifyButtons);
  } else {
    startKbiteNotifyButtons();
  }

  document.addEventListener('shopify:section:load', startKbiteNotifyButtons);
})();
